import { createContext, ReactNode, useEffect, useReducer, useState } from 'react';
import useCustomDispatch from 'redux/dispatch';
import { purgePersistedUserData, RootReducerTypes } from 'redux/rootReducer';
import { User } from '../@types/account';
import {
  ActionMap,
  AuthState,
  AuthUser,
  JWTContextType,
  LogoutParams,
  NewPassword,
  Password,
  UserSetup
} from '../@types/authentication';
import { RootState, useSelector } from '../redux/store';
import { axiosAmApiInstance as amApi } from '../utils/axios';
import {
  getApplicationClaims,
  getUserRole,
  getTokenScope,
  isValidToken,
  setProvisionalSession,
  setSession
} from '../utils/jwt';
import { useNavigate } from 'react-router';
import { Organisation } from '../@types/organization';
import useDisplayMessage from 'components/messages/useMessage';
import MfaRequiredDialog from 'components/mfa/MfaRequiredDialog';
import { useTranslator } from 'translation/useTranslator';
import {
  loginWithPassword,
  loginWithExternalToken,
  loginWithTenantChange,
  loginWithMfaVerification,
  fetchAccessibleTenants,
  fetchCurrentUser,
  mfaSetup,
  mfaVerify,
  mfaDisable,
  refreshAccessToken,
  clearTokens,
  getRefreshToken,
  MfaRequiredError
} from '../utils/oidcTokenService';

enum Types {
  Initial = 'INITIALIZE',
  Login = 'LOGIN',
  Logout = 'LOGOUT',
  Register = 'REGISTER'
}

type JWTAuthPayload = {
  [Types.Initial]: {
    isAuthenticated: boolean;
    user: AuthUser;
  };
  [Types.Login]: {
    user: AuthUser;
  };
  [Types.Logout]: undefined;
  [RootReducerTypes.RESET_STATES]: undefined;
  [Types.Register]: {
    user: AuthUser;
  };
};

export type JWTActions = ActionMap<JWTAuthPayload>[keyof ActionMap<JWTAuthPayload>];

const initialState: AuthState = {
  isAuthenticated: false,
  isInitialized: false,
  user: null
};

const JWTReducer = (state: AuthState, action: JWTActions) => {
  switch (action.type) {
    case 'INITIALIZE':
      return {
        isAuthenticated: action.payload.isAuthenticated,
        isInitialized: true,
        user: action.payload.user
      };
    case 'LOGIN':
      return {
        ...state,
        isAuthenticated: true,
        user: action.payload.user
      };
    case 'LOGOUT':
      return {
        ...state,
        isAuthenticated: false,
        user: null
      };

    case 'REGISTER':
      return {
        ...state,
        isAuthenticated: true,
        user: action.payload.user
      };

    default:
      return state;
  }
};

const AuthContext = createContext<JWTContextType | null>(null);

function AuthProvider({ children }: { children: ReactNode }) {
  const translator = useTranslator();
  const customDispatch = useCustomDispatch();
  const displayMessage = useDisplayMessage();
  const [state, reducerDispatch] = useReducer(JWTReducer, initialState);
  const { organizationProfile } = useSelector((state: RootState) => state.organization);
  const [rerenderOnOrgChange, setRerenderOnOrgChange] = useState<number>(1);
  const [selectedOrg, setSelectedOrg] = useState<Organisation | undefined>();
  const navigate = useNavigate();
  const [mfaDialogOpen, setMfaDialogOpen] = useState(false);
  const [userPhoneNumber, setUserPhoneNumber] = useState<string>('');

  useEffect(() => {
    const initialize = async () => {
      try {
        let accessToken = globalThis.localStorage.getItem('accessToken');

        const accessTokenExpired = !accessToken || !isValidToken(accessToken);
        const refreshToken = getRefreshToken();
        const accessTokenExpiredButRefreshTokenAvailable = accessTokenExpired && refreshToken;
        if (accessTokenExpiredButRefreshTokenAvailable) {
          const tokenResponse = await refreshAccessToken();
          if (tokenResponse) {
            accessToken = tokenResponse.access_token;
          }
        }

        if (accessToken && isValidToken(accessToken)) {
          let claims = getApplicationClaims(accessToken);
          setSession(accessToken);

          let user = await getUserProfile();
          user!.claims = claims;
          user!.role = getUserRole(accessToken);

          setUserPhoneNumber(user!.phoneNumber);
          reducerDispatch({
            type: Types.Initial,
            payload: {
              isAuthenticated: true,
              user
            }
          });
        } else {
          clearTokens();
          reducerDispatch({
            type: Types.Initial,
            payload: {
              isAuthenticated: false,
              user: null
            }
          });
        }
      } catch (err) {
        console.error(err);
        reducerDispatch({
          type: Types.Initial,
          payload: {
            isAuthenticated: false,
            user: null
          }
        });
      }
    };

    initialize();
  }, []);

  const getUserProfile = async (): Promise<AuthUser> => {
    return fetchCurrentUser();
  };

  const fetchTenantsAndMaybeAutoSelect = async (): Promise<Organisation[]> => {
    const tenants = await fetchAccessibleTenants();
    const hasSingleTenantAccess = tenants.length === 1;
    if (hasSingleTenantAccess) {
      await selectTenant(tenants[0].organizationId);
      return [];
    }

    return tenants;
  };

  const login = async (
    username: string,
    password: string,
    mfaCode?: string
  ): Promise<Organisation[]> => {
    if (mfaCode) {
      const mfaResult = await loginWithMfaVerification(mfaCode);
      setProvisionalSession(mfaResult.provisionalToken);
      return fetchTenantsAndMaybeAutoSelect();
    }

    const result = await loginWithPassword(username, password);

    setProvisionalSession(result.provisionalToken);
    const scopes = getTokenScope(result.provisionalToken);
    if (scopes.includes('mfa_required')) {
      throw new MfaRequiredError('MfaCodeRequired');
    }

    return fetchTenantsAndMaybeAutoSelect();
  };

  const loginExternal = async (idToken: string): Promise<Organisation[]> => {
    const result = await loginWithExternalToken(idToken);
    setProvisionalSession(result.provisionalToken);

    return fetchTenantsAndMaybeAutoSelect();
  };

  const selectTenant = async (organizationId: string, rerenderOnOrgChange?: boolean) => {
    const tenantResult = await loginWithTenantChange(organizationId);
    const accessToken = tenantResult.tokenResponse.access_token;

    setSession(accessToken);

    const isNewOrg = organizationProfile?.id && organizationProfile?.id !== organizationId;
    if (isNewOrg) {
      await customDispatch({
        actionParameters: { type: RootReducerTypes.RESET_STATES },
        disableAllMessages: true
      });
    }

    if (!organizationProfile?.id || organizationProfile.id !== organizationId) {
      const currentPath = globalThis.location.pathname;
      const paths = currentPath?.split('/');
      let newPath = '';

      for (const path of paths ?? []) {
        if (Number.isNaN(Number.parseFloat(path))) {
          newPath += `/${path}`;
          continue;
        }
        break;
      }
      navigate(newPath);
    }

    let applicationClaims = getApplicationClaims(accessToken);

    getUserProfile()
      .then((user) => {
        user!.claims = applicationClaims;
        user!.role = getUserRole(accessToken);
        reducerDispatch({
          type: Types.Login,
          payload: {
            user
          }
        });

        customDispatch({ actionParameters: { type: Types.Login }, disableAllMessages: true });
      })
      .catch((err) => {
        console.error(err);
        return logout();
      })
      .catch(console.error);

    if (rerenderOnOrgChange) {
      setRerenderOnOrgChange((prevState) => prevState + 1);
    }
  };

  const signUp = (newUser: UserSetup) => {
    return async () => {
      try {
        const response = await amApi.post('/authorization/reset-password', newUser);
        return await Promise.resolve({
          result: response,
          defaultSuccessMessage: translator.newAccountSuccess()
        });
      } catch (error: any) {
        return Promise.reject({
          error: error,
          defaultErrorMessage: translator.newAccountError()
        });
      }
    };
  };

  const logout = async (params?: LogoutParams) => {
    reducerDispatch({ type: Types.Logout });
    customDispatch({
      actionParameters: { type: RootReducerTypes.LOGOUT },
      disableAllMessages: true
    });
    purgePersistedUserData().catch(console.error);
    setSession(null);
    clearTokens();

    if (params?.logoutInfoMessage) {
      displayMessage.info({
        message: params.logoutInfoMessage,
        autoHideDuration: 10000
      });
    }
  };

  const forgotPassword = (email: string) => {
    return async () => {
      try {
        const response = await amApi.post('/authorization/forgot-password', {
          email
        });
        return await Promise.resolve({
          result: response,
          defaultSuccessMessage: translator.passwordRecoverySuccess()
        });
      } catch (error: any) {
        return Promise.reject({
          error: error,
          defaultErrorMessage: translator.passwordRecoveryError()
        });
      }
    };
  };

  const resetPassword = (password: NewPassword) => {
    return async () => {
      try {
        const payload = {
          userId: password.userId,
          resetToken: password.resetToken,
          newPassword: password.newPassword,
          confirmNewPassword: password.confirmNewPassword
        };
        const response = await amApi.post('/authorization/reset-password', payload);
        return await Promise.resolve({
          result: response,
          defaultSuccessMessage: translator.passwordUpdateSuccess()
        });
      } catch (error: any) {
        return Promise.reject({
          error: error,
          defaultErrorMessage: translator.passwordUpdateError()
        });
      }
    };
  };

  const updateProfile = async (updatedUser: User) => {
    if (updatedUser.phoneNumber?.length === 0) {
      updatedUser.phoneNumberConfirmed = false;
    }

    const response = await amApi.put('/users/current', updatedUser);

    const user = response.data;
    const accessToken = globalThis.localStorage.getItem('accessToken');
    if (accessToken && isValidToken(accessToken)) {
      user!.claims = getApplicationClaims(accessToken);
      user!.role = getUserRole(accessToken);
    }

    const payLoad = {
      user: { ...user, phoneNumberConfirmed: updatedUser.phoneNumberConfirmed }
    };
    reducerDispatch({
      type: Types.Login,
      payload: payLoad
    });
  };

  const updatePassword = async (passwords: Password) => {
    const userId = state.user?.userId;
    if (!userId) {
      return Promise.reject(new Error(translator.updatePasswordUserIsNotLoggedInError()));
    }
    await amApi.post('/authorization/change-password', {
      currentPassword: passwords.oldPassword,
      newPassword: passwords.newPassword,
      confirmNewPassword: passwords.confirmNewPassword
    });
  };

  const editUser = (updatedUser: Partial<AuthUser>) => {
    const currentUser = state.user;

    if (!currentUser) {
      console.error(translator.editUserUserIsNotLoggedInError());
      return;
    }

    const newUser = {
      ...currentUser,
      ...updatedUser
    };

    reducerDispatch({
      type: Types.Login,
      payload: {
        user: newUser
      }
    });
  };

  const updateMultiFactorEnabled = async (enable: boolean, acceptedPhoneNumber: string) => {
    if (enable) {
      setUserPhoneNumber(acceptedPhoneNumber);
      setMfaDialogOpen(true);
      return;
    }

    await mfaDisable();
    editUser({
      multiFactorEnabled: false
    });
  };

  const finalizeMfaActivation = async (enable: boolean, acceptedPhoneNumber: string) => {
    editUser({
      multiFactorEnabled: enable,
      phoneNumber: acceptedPhoneNumber,
      phoneNumberConfirmed: true
    });
  };

  const updatePhoneNumber = async (phoneNumber: string, authCode?: string) => {
    try {
      if (authCode) {
        await mfaVerify(authCode);
      } else {
        await mfaSetup(phoneNumber);
      }
    } catch (error: any) {
      throw error.response?.data?.errors ?? error.message ?? error;
    }
  };

  return (
    <>
      <AuthContext.Provider
        value={{
          ...state,
          rerenderOnOrgChange: rerenderOnOrgChange,
          method: 'jwt',
          login,
          loginExternal,
          selectTenant,
          logout,
          signUp,
          forgotPassword,
          resetPassword,
          updateProfile,
          updatePassword,
          updateMultiFactorEnabled,
          updatePhoneNumber,
          selectedOrg,
          setSelectedOrg,
          isDataReflectingCurrentOrg: !(
            state.isAuthenticated &&
            (!organizationProfile?.id ||
              Boolean(
                selectedOrg?.organizationId &&
                  organizationProfile?.id !== selectedOrg?.organizationId
              ))
          )
        }}
      >
        {children}
      </AuthContext.Provider>
      <MfaRequiredDialog
        open={mfaDialogOpen}
        setOpen={setMfaDialogOpen}
        userPhoneNumber={userPhoneNumber}
        updatePhoneNumber={updatePhoneNumber}
        updateMultiFactorEnabled={finalizeMfaActivation}
      />
    </>
  );
}

export { AuthContext, AuthProvider };
